import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { debounce, get } from 'lodash';
import { colors } from '../../constants/styleConstants';
import {
  MEDIA,
  MEDIA_ATTRIBUTES,
  PRIVACY_OPTIONS,
  PRODUCTIONS,
  PRODUCTIONS_AGGREGATIONS,
} from '../../constants/endpointConstants';
import {
  MEDIA_ATTRIBUTES_TYPES_IDS,
  MEDIA_TAGS_TYPES_IDS,
  MEDIA_TYPES_IDS,
  performancesAggregationTypes,
  PRIVACY_ATTRIBUTE_IDS,
  TAGS_AVAILABILITY_TYPES,
  TP,
} from '../../constants/consts';
import { EDITING_ENTITY_TYPES, getMediaTagsWithoutEntityTag } from '../../utils/editCommon';
import {
  createEntity,
  getAggregationsAction,
  getList,
  getPrivacyOptionsAction,
  getWholeList,
  updateOne,
} from '../App/actions';
import EditMediaLayout from '../../components/EditComponents/EditMediaLayout';
import FbCircularProgress from '../../components/FbCircularProgress';
import EditMediaCard from '../../components/EditComponents/EditMediaCard';
import AddMediaDetailsModal from '../../components/EditComponents/AddMediaDetailsModal';
import AddImageModal from '../../components/EditComponents/AddImageModal';
import ConfirmDeleteModal from '../../components/EditComponents/ConfirmDeleteModal';
import {
  createImageAction,
  deleteImageAction,
  getEditImageProductionsAction,
  getImageFormatAttrTypesAction,
  getImagesAction,
  getImagesMediaAttrTypesAction,
  updateImageAction,
} from './actions';
import {
  selectImages,
  selectMediaAttrTypesOptions,
  selectImageFormatsNames,
  selectUpdatingImageName,
  selectImageProductions,
} from './selectors';
import { selectAggregationsLoading, selectAggregationsOptions, selectPrivacyOptions } from '../App/selectors';

const useStyles = makeStyles(() => ({
  error: {
    color: colors.red,
  },
}));

const typesData = {
  [EDITING_ENTITY_TYPES.COMPANY]: {
    mediaTagTypeId: MEDIA_TAGS_TYPES_IDS.COMPANY,
    aggTypeName: performancesAggregationTypes.companyAgg,
  },
  [EDITING_ENTITY_TYPES.FESTIVAL]: {
    mediaTagTypeId: MEDIA_TAGS_TYPES_IDS.FESTIVAL,
    aggTypeName: performancesAggregationTypes.festivalAgg,
  },
  [EDITING_ENTITY_TYPES.ARTIST]: {
    mediaTagTypeId: MEDIA_TAGS_TYPES_IDS.PROFILE,
    aggTypeName: performancesAggregationTypes.profileAgg,
  },
};

const EditImages = ({
  entity,
  type,
  title,
  subtitle,
  privacyOptions,
  imageAttributesOptions,
  images,
  updatingImageName,
  imageFormats,
  productions,
  productionsMWList,
  loadingFieldName,
  children,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isAddModalOpened, setAddModalState] = React.useState(false);
  const [isEditModalOpened, setEditModalState] = React.useState(false);
  const [editModalPhoto, setEditModalPhoto] = React.useState(null);
  const [photoToDelete, setPhotoToDelete] = React.useState(null);
  const mediaTagData = get(typesData, type, {});

  React.useEffect(() => {
    if (!images?.loadedOnce || entity?.id !== images?.criteria?.mediaTagId) {
      dispatch(
        getWholeList({
          endpoint: MEDIA,
          sagaRoutine: getImagesAction,
          params: {
            queryParams: {
              media_type_id: MEDIA_TYPES_IDS.IMAGE,
              media_tag_type_id: mediaTagData?.mediaTagTypeId,
              media_tag_id: entity?.id,
            },
          },
        }),
      );
    }
    if (!imageAttributesOptions?.length) {
      dispatch(
        getWholeList({
          endpoint: MEDIA_ATTRIBUTES,
          sagaRoutine: getImagesMediaAttrTypesAction,
          params: {
            queryParams: {
              attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE,
            },
          },
        }),
      );
    }
    if (!imageFormats?.length) {
      dispatch(
        getWholeList({
          endpoint: MEDIA_ATTRIBUTES,
          sagaRoutine: getImageFormatAttrTypesAction,
          params: {
            queryParams: {
              attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE_FORMAT,
            },
          },
        }),
      );
    }
    if (!privacyOptions?.length) {
      dispatch(
        getWholeList({
          endpoint: PRIVACY_OPTIONS,
          sagaRoutine: getPrivacyOptionsAction,
          params: {},
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddPhotoModal = () => {
    setAddModalState(true);
  };

  const closeAddPhotoModal = () => {
    setAddModalState(false);
  };

  const openEditPhotoModal = chosenPhoto => {
    setEditModalState(true);
    setEditModalPhoto(chosenPhoto);
  };

  const closeEditPhotoModal = () => {
    setEditModalState(false);
    setEditModalPhoto(null);
  };

  const uploadNewPhoto = file => {
    dispatch(
      createEntity({
        endpoint: MEDIA,
        sagaRoutine: createImageAction,
        params: {
          ...(file?.type === 'url' ? { url: file?.src } : { file: { data: `@${file?.file?.name}` } }),
          mediaType: {
            id: MEDIA_TYPES_IDS.IMAGE,
          },
          privacyAttribute: {
            id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
          },
          tags: [
            {
              tagType: { id: mediaTagData?.mediaTagTypeId }, // TODO change
              tagId: entity?.id,
              availabilityTypes: [TAGS_AVAILABILITY_TYPES.GALLERY],
            },
          ],
          description: file?.title,
        },
        files: { [file?.file?.name]: file?.file },
        callback: closeAddPhotoModal,
      }),
    );
  };

  const changePrivacy = (v, id) => {
    dispatch(
      updateOne({
        endpoint: MEDIA,
        id,
        sagaRoutine: updateImageAction,
        params: {
          privacyAttribute: { id: v?.value },
        },
      }),
    );
  };

  const updateMedia = (data, id) => {
    closeEditPhotoModal();
    if (id) {
      dispatch(
        updateOne({
          endpoint: MEDIA,
          id,
          sagaRoutine: updateImageAction,
          params: data,
        }),
      );
    }
  };

  const closeConfirmationModal = () => {
    setPhotoToDelete(null);
  };

  const deleteImage = () => {
    closeConfirmationModal();
    const photoTags = getMediaTagsWithoutEntityTag(photoToDelete?.tags, mediaTagData?.mediaTagTypeId, entity?.id);
    dispatch(
      updateOne({
        endpoint: MEDIA,
        id: photoToDelete?.id,
        sagaRoutine: deleteImageAction,
        params: {
          tags: photoTags,
        },
      }),
    );
  };

  const getProductions = (pagination, query = {}) => {
    dispatch(
      getList({
        endpoint: PRODUCTIONS,
        sagaRoutine: getEditImageProductionsAction,
        params: {
          queryParams: {
            [mediaTagData?.aggTypeName]: entity?.id,
            sort: '-year',
            ...query,
          },
          pagination,
        },
      }),
    );
  };

  const searchProductions = debounce(v => {
    dispatch(
      getAggregationsAction({
        endpoint: PRODUCTIONS_AGGREGATIONS,
        query: {
          [performancesAggregationTypes.typeAgg]: performancesAggregationTypes.workAgg,
          [mediaTagData?.aggTypeName]: entity?.id,
          [performancesAggregationTypes.queryAgg]: v,
        },
        fieldName: 'production-search',
      }),
    );
  }, 300);

  return (
    <>
      <EditMediaLayout
        title={title}
        subtitle={subtitle}
        addButtonClick={openAddPhotoModal}
        buttonTitle={`edit:${TP}.FN_EDITARTIST_ADD_IMAGE`}
        isEmpty={images?.data?.length === 0 && images?.loadedOnce}
      >
        {children}
        <section className={classes.cardsSection}>
          {get(images, 'loading', false) && !get(images, 'loadedOnce', false) ? (
            <FbCircularProgress />
          ) : (
            Boolean(images.data?.length) &&
            get(images, 'data', []).map(photo => (
              <EditMediaCard
                media={photo}
                editClick={() => openEditPhotoModal(photo)}
                key={photo?.id}
                privacyOptions={privacyOptions}
                mediaType="image"
                onPrivacyChange={changePrivacy}
                onDelete={() => setPhotoToDelete({ ...photo, imageType: 'image' })}
                loading={updatingImageName === photo?.id}
              />
            ))
          )}
        </section>
      </EditMediaLayout>
      {isEditModalOpened && (
        <AddMediaDetailsModal
          isOpened={isEditModalOpened}
          onClose={closeEditPhotoModal}
          submitFunc={updateMedia}
          entity={editModalPhoto}
          typeOptions={imageAttributesOptions}
          type="image"
          getProductions={getProductions}
          productionsData={productions}
          searchProductions={searchProductions}
          loadingFieldName={loadingFieldName}
          productionsMWList={productionsMWList}
        />
      )}
      {isAddModalOpened && (
        <AddImageModal
          isOpened={isAddModalOpened}
          onClose={closeAddPhotoModal}
          uploadPhoto={uploadNewPhoto}
          title={`edit:${TP}.FN_EDITARTIST_ADD_IMAGE`}
          isLoading={updatingImageName === 'new'}
          formats={imageFormats}
        />
      )}
      {photoToDelete && (
        <ConfirmDeleteModal
          closeModal={closeConfirmationModal}
          title={t(`${TP}.FN_DELETE_ARTIST_IMAGE_CONFIRMATION`)}
          onDelete={deleteImage}
        />
      )}
    </>
  );
};

EditImages.propTypes = {
  entity: PropTypes.object.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  updatingImageName: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.oneOf([EDITING_ENTITY_TYPES.FESTIVAL, EDITING_ENTITY_TYPES.COMPANY]),
  images: PropTypes.shape({
    data: PropTypes.array,
    loading: PropTypes.bool,
  }),
  productions: PropTypes.array,
  productionsMWList: PropTypes.array,
  loadingFieldName: PropTypes.string,
};

EditImages.defaultProps = {
  title: `${TP}.FN_STAFF_TITLE`,
};

const mapStateToProps = createStructuredSelector({
  images: selectImages(),
  imageAttributesOptions: selectMediaAttrTypesOptions(),
  imageFormats: selectImageFormatsNames(),
  updatingImageName: selectUpdatingImageName(),
  privacyOptions: selectPrivacyOptions(),
  productions: selectImageProductions(),
  productionsMWList: selectAggregationsOptions(performancesAggregationTypes.workAgg),
  loadingFieldName: selectAggregationsLoading(),
});

const withConnect = connect(mapStateToProps, null);
export default withConnect(EditImages);
